import { startCase } from 'lodash';

import type { ChatItinerarySnapshot, ChatSnapshotMessage, ChatTraceResponse } from '~/services/TripPlannerService';

export function getSnapshotMessageId(
  turn: ChatTraceResponse['result']['turns'][number],
  messages: ChatSnapshotMessage[],
) {
  // Trace ledger message IDs identify model runs, not persisted chat messages.
  // Match the user anchor to the next saved assistant message instead.
  const user = turn.entries.find((entry) => entry.role === 'user');
  const orderedMessages = [...messages].sort((a, b) => a.messageSeq - b.messageSeq);
  const userIndex = user
    ? orderedMessages.findIndex((message) => message.id === user.id && message.role === 'USER')
    : -1;
  const assistant = user
    ? userIndex >= 0
      ? orderedMessages[userIndex + 1]
      : undefined
    : orderedMessages.find((message) =>
        turn.entries.some((entry) => entry.role === 'assistant' && 'messageSeq' in entry && entry.id === message.id),
      );
  return assistant?.role === 'ASSISTANT' && assistant.generationStatus === 'COMPLETED' ? assistant.id : undefined;
}

export function snapshotLines(snapshot: ChatItinerarySnapshot): string[] {
  const lines: string[] = [];
  const stops = new Map(snapshot.stops.map((stop) => [stop.id, stop.placeName]));
  for (const [index, stop] of snapshot.stops.entries()) {
    const location = `${index + 1}. ${stop.placeName}`;
    lines.push(`${location}${stop.numberOfNights == null ? '' : ` · ${stop.numberOfNights} nights`}`);
    const items = new Map(
      [...stop.itemsWithoutDay, ...stop.days.flatMap((day) => day.items)].map((item) => [item.id, item]),
    );
    for (const item of items.values()) lines.push(`${location} · ${startCase(item.type.toLowerCase())}: ${item.name}`);
    for (const transport of stop.transports ?? []) {
      const route = `${stops.get(transport.departureStopId) ?? 'Unknown stop'} → ${stops.get(transport.arrivalStopId) ?? 'Unknown stop'}`;
      for (const option of transport.options)
        lines.push(`${route} · ${startCase(option.type.toLowerCase())}: ${option.name || option.label}`);
    }
  }
  return lines;
}

export function snapshotDifference(lines: string[], against: string[]) {
  const counts = new Map<string, number>();
  against.forEach((line) => counts.set(line, (counts.get(line) ?? 0) + 1));
  return lines.filter((line) => {
    const count = counts.get(line) ?? 0;
    if (!count) return true;
    counts.set(line, count - 1);
    return false;
  });
}
